
import React, { useState, useEffect, useRef } from 'react';
import {
  X, Save, Loader2, Edit3, MapPin, Building2,
  Activity, Info, Shield, UserCheck, ChevronDown, Check, User, Search, Trash2, Car,
  Flame, Zap, Cloud, Link, Image, Euro, Calendar, Thermometer, Plus
} from 'lucide-react';
import { supabase } from '../../../lib/supabase/client';
import { eigentuemerAnzeigename, istFirma, ansprechpartnerZusatz } from '../../../lib/eigentuemer/anzeige';
import { Objekt } from './ObjekteListe';
import { DETAIL_FLAECHE } from '../../../lib/ui/detailFlaeche';
import { setzeObjektEigentuemerListe } from '../../../lib/eigentuemer/verknuepfung';

interface ObjektEditSidebarProps {
  objekt: Objekt | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface TeamMember { id: string; full_name: string; }
interface Team { id: string; name: string; }
interface Eigentuemer { id: string; anrede?: string | null; vorname: string | null; nachname: string | null; firma_name?: string | null; email: string | null; }

const ObjektEditSidebar: React.FC<ObjektEditSidebarProps> = ({ objekt, isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [allEigentuemer, setAllEigentuemer] = useState<Eigentuemer[]>([]);
  const [assignedEigentuemer, setAssignedEigentuemer] = useState<Eigentuemer[]>([]);
  const [formData, setFormData] = useState<Record<string, any>>({});

  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);
  const [isOwnerSearchOpen, setIsOwnerSearchOpen] = useState(false);
  const [ownerSearchQuery, setOwnerSearchQuery] = useState('');

  const assigneeRef = useRef<HTMLDivElement>(null);
  const ownerSearchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && objekt) {
      fetchTeamMembers();
      fetchTeams();
      fetchAllEigentuemer();
      fetchAssignedOwners();
      setFormData({ ...objekt });
    }
  }, [isOpen, objekt?.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (assigneeRef.current && !assigneeRef.current.contains(event.target as Node)) setIsAssigneeDropdownOpen(false);
      if (ownerSearchRef.current && !ownerSearchRef.current.contains(event.target as Node)) setIsOwnerSearchOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTeamMembers = async () => {
    const { data } = await supabase.from('profiles').select('id, full_name').or('role.eq.team,role.eq.admin').eq('zuweisbar', true).order('full_name');
    setTeamMembers(data || []);
  };

  const fetchTeams = async () => {
    const { data } = await supabase.from('teams').select('id, name').order('name');
    setTeams(data || []);
  };

  const fetchAllEigentuemer = async () => {
    const { data } = await supabase.from('eigentuemer').select('id, anrede, vorname, nachname, firma_name, email').order('nachname');
    setAllEigentuemer(data || []);
  };

  const fetchAssignedOwners = async () => {
    if (!objekt) return;
    const { data } = await supabase.from('v_objekt_eigentuemer').select('*').eq('objekt_id', objekt.id);
    setAssignedEigentuemer(data || []);
  };

  const handleAddOwner = (owner: Eigentuemer) => {
    if (assignedEigentuemer.find(e => e.id === owner.id)) return;
    setAssignedEigentuemer(prev => [...prev, owner]);
    setIsOwnerSearchOpen(false);
    setOwnerSearchQuery('');
  };

  const handleRemoveOwner = (id: string) => setAssignedEigentuemer(prev => prev.filter(e => e.id !== id));

  const handleSubmit = async () => {
    if (!objekt) return;
    setLoading(true);
    try {
      // Nur bekannte DB-Spalten senden
      const DB_FIELDS = [
        'bezeichnung', 'objektnummer', 'objekttyp', 'status',
        'strasse', 'hausnummer', 'plz', 'stadt', 'etage',
        'wohneinheiten', 'baujahr', 'wohnflaeche', 'miete_soll',
        'heizungsart', 'energieausweis', 'energieklasse', 'letzte_pruefung', 'ausstattung', 'notizen',
        'stellplatz_typ', 'stellplatz_anzahl',
        'onedrive_link', 'bild_url',
        'zustaendig', 'zustaendig_id', 'zustaendig_typ', 'eigentuemer', 'eigentuemer_id'
      ];

      const d: Record<string, any> = {};
      DB_FIELDS.forEach(f => {
        if (f in formData) d[f] = formData[f];
      });

      // Numerische Felder bereinigen
      ['wohneinheiten', 'baujahr', 'stellplatz_anzahl'].forEach(f => {
        if (d[f] === '' || d[f] === undefined) d[f] = f === 'baujahr' ? null : 0;
      });

      // Dezimalfelder bereinigen (Komma → Punkt, Tausendertrenner entfernen)
      ['wohnflaeche', 'miete_soll'].forEach(f => {
        if (typeof d[f] === 'string') {
          // "5.400,50" → "5400.50" / "5400" → "5400"
          let v = d[f].replace(/[^0-9,.]/g, '');
          if (v.includes(',')) {
            // Komma als Dezimaltrenner: Punkte (Tausender) entfernen, Komma → Punkt
            v = v.replace(/\./g, '').replace(',', '.');
          }
          d[f] = v ? parseFloat(v) : null;
        }
      });

      // objekte.eigentuemer/-_id pflegt der Cache-Trigger aus objekt_eigentuemer —
      // hier bewusst NICHT mitschreiben (wuerde ohnehin ueberschrieben).

      const { error } = await supabase.from('objekte').update(d).eq('id', objekt.id);
      if (error) throw error;

      // Eigentuemer ueber die eine Quelle setzen; entity_links spiegelt der Trigger.
      await setzeObjektEigentuemerListe(objekt.id, assignedEigentuemer.map((e) => e.id));
      onSuccess();
      onClose();
    } catch (err: any) {
      alert('Fehler: ' + (err.message || 'Unbekannter Fehler'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ['wohneinheiten', 'baujahr', 'stellplatz_anzahl'].includes(name) ? (value === '' ? '' : parseInt(value)) : value
    }));
  };

  const filteredSearch = allEigentuemer.filter(e =>
    `${eigentuemerAnzeigename(e)} ${e.vorname || ''} ${e.nachname || ''} ${e.firma_name || ''}`.toLowerCase().includes(ownerSearchQuery.toLowerCase()) &&
    !assignedEigentuemer.find(ae => ae.id === e.id)
  );

  const getDisplayAddress = () => {
    const street = `${formData.strasse || ''} ${formData.hausnummer || ''}`.trim();
    const city = `${formData.plz || ''} ${formData.stadt || ''}`.trim();
    return [street, city].filter(Boolean).join(', ') || 'Keine Adresse';
  };

  if (!objekt) return null;

  return (
    <>
      <div data-objekt-modal className={`${DETAIL_FLAECHE} md:w-[440px] bg-[#faf9f7] shadow-[-12px_0_40px_rgba(0,0,0,0.06)] flex flex-col transition-transform duration-500 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>

        {/* ── Dark Header ── */}
        <div className="relative overflow-hidden shrink-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]" />
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIxIiBmaWxsPSJyZ2JhKDE5NiwxNjcsMTI1LDAuMDgpIi8+PC9zdmc+')] opacity-60" />
          <div className="relative p-5 pb-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded-full text-[7px] font-black uppercase tracking-wider bg-[#c4a77d]/20 text-[#c4a77d] ring-1 ring-[#c4a77d]/20 backdrop-blur-sm">
                  Bearbeiten
                </span>
                <span className="text-[7px] font-black text-white/30 uppercase tracking-widest flex items-center gap-1">
                  <Building2 size={8} /> Objekt
                </span>
              </div>
              <button onClick={onClose} className="p-2 rounded-xl bg-white/10 text-white/40 hover:text-white hover:bg-white/20 transition-all backdrop-blur-sm">
                <X size={14} />
              </button>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#c4a77d] to-[#b8956a] flex items-center justify-center text-white shadow-lg shadow-[#c4a77d]/20 ring-2 ring-white/10 shrink-0">
                <Edit3 size={20} />
              </div>
              <div className="min-w-0">
                <h2 className="text-[15px] font-black text-white truncate leading-tight">{formData.bezeichnung || 'Objekt'}</h2>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <MapPin size={10} className="text-[#c4a77d] shrink-0" />
                  <p className="text-[10px] text-white/40 truncate">{getDisplayAddress()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Scrollable Content ── */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">

          {/* Stammdaten */}
          <Section title="Stammdaten" icon={<Building2 size={13} className="text-[#c4a77d]" />}>
            <div className="space-y-3">
              <SidebarInput label="Bezeichnung" name="bezeichnung" value={formData.bezeichnung} onChange={handleChange} required />
              <SidebarInput label="Objektnummer" name="objektnummer" value={formData.objektnummer} onChange={handleChange} required />
              <div className="grid grid-cols-2 gap-2">
                <SidebarSelect label="Objekttyp" name="objekttyp" value={formData.objekttyp} onChange={handleChange}
                  options={['Mehrfamilienhaus', 'Einfamilienhaus', 'Mietverwaltung', 'WEG', 'Sondereigentum', 'Teileigentum', 'Gewerbe', 'Neubau']} />
                <SidebarSelect label="Status" name="status" value={formData.status} onChange={handleChange}
                  options={['Aktiv', 'Inaktiv']} />
              </div>
              <div className="relative" ref={assigneeRef}>
                <label className="text-[8px] font-black text-[#c4a77d] uppercase tracking-widest mb-1 block">Zuständigkeit</label>
                <button type="button" onClick={() => setIsAssigneeDropdownOpen(!isAssigneeDropdownOpen)}
                  className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-900 outline-none flex items-center justify-between hover:border-[#c4a77d]/40 transition-colors">
                  <div className="flex items-center gap-2">
                    <UserCheck size={12} className="text-[#c4a77d]" />
                    <span className="truncate">{formData.zustaendig || 'Global'}</span>
                  </div>
                  <ChevronDown size={12} className={`text-gray-300 transition-transform duration-200 ${isAssigneeDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {isAssigneeDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-100 z-50 max-h-[150px] overflow-y-auto custom-scrollbar animate-in zoom-in-95 origin-top duration-150">
                    <button type="button" onClick={() => { setFormData(p => ({ ...p, zustaendig: 'Global', zustaendig_id: null, zustaendig_typ: null })); setIsAssigneeDropdownOpen(false); }}
                      className="w-full p-2.5 text-left text-[10px] font-bold text-gray-400 hover:bg-[#fdfbf8] hover:text-gray-900 transition-colors">Global</button>
                    {teamMembers.length > 0 && <div className="px-2.5 pt-2 pb-1 text-[8px] font-black text-gray-300 uppercase tracking-[0.2em]">Mitarbeiter</div>}
                    {teamMembers.map(m => (
                      <button key={m.id} type="button" onClick={() => { setFormData(p => ({ ...p, zustaendig: m.full_name, zustaendig_id: m.id, zustaendig_typ: 'mitarbeiter' })); setIsAssigneeDropdownOpen(false); }}
                        className="w-full p-2.5 flex items-center justify-between text-[10px] font-bold text-gray-400 hover:bg-[#fdfbf8] hover:text-gray-900 transition-colors">
                        {m.full_name} {formData.zustaendig_id === m.id && formData.zustaendig_typ !== 'team' && <Check size={10} className="text-[#c4a77d]" />}
                      </button>
                    ))}
                    {teams.length > 0 && <div className="px-2.5 pt-2 pb-1 text-[8px] font-black text-gray-300 uppercase tracking-[0.2em]">Teams</div>}
                    {teams.map(t => (
                      <button key={t.id} type="button" onClick={() => { setFormData(p => ({ ...p, zustaendig: t.name, zustaendig_id: t.id, zustaendig_typ: 'team' })); setIsAssigneeDropdownOpen(false); }}
                        className="w-full p-2.5 flex items-center justify-between text-[10px] font-bold text-gray-400 hover:bg-[#fdfbf8] hover:text-gray-900 transition-colors">
                        {t.name} {formData.zustaendig_id === t.id && formData.zustaendig_typ === 'team' && <Check size={10} className="text-[#c4a77d]" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Section>

          {/* Adresse */}
          <Section title="Adresse" icon={<MapPin size={13} className="text-[#c4a77d]" />}>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2"><SidebarInput label="Straße" name="strasse" value={formData.strasse} onChange={handleChange} /></div>
                <SidebarInput label="Nr." name="hausnummer" value={formData.hausnummer} onChange={handleChange} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <SidebarInput label="PLZ" name="plz" value={formData.plz} onChange={handleChange} />
                <div className="col-span-2"><SidebarInput label="Stadt" name="stadt" value={formData.stadt} onChange={handleChange} /></div>
              </div>
              <SidebarInput label="Etage" name="etage" value={formData.etage} onChange={handleChange} placeholder="z.B. EG, 1. OG, DG" />
            </div>
          </Section>

          {/* Eigentümer */}
          <Section title="Eigentümer" icon={<Shield size={13} className="text-[#c4a77d]" />} badge={assignedEigentuemer.length > 0 ? `${assignedEigentuemer.length}` : undefined}>
            <div className="space-y-2">
              {assignedEigentuemer.map(owner => (
                <div key={owner.id} className="flex items-center justify-between p-2 bg-white rounded-xl border border-gray-100 group animate-in fade-in duration-200">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-[#fdfbf8] flex items-center justify-center text-[#c4a77d] text-[8px] font-black border border-gray-50 shrink-0">
                      {istFirma(owner) ? <Building2 size={12} /> : `${owner.vorname?.[0] || ''}${owner.nachname?.[0] || ''}`}
                    </div>
                    <div className="min-w-0">
                      <span className="text-[10px] font-bold text-gray-800 truncate block">{eigentuemerAnzeigename(owner)}</span>
                      {ansprechpartnerZusatz(owner)
                        ? <span className="text-[9px] font-medium text-gray-400 truncate block">{ansprechpartnerZusatz(owner)}</span>
                        : <span className="text-[7px] text-gray-400 truncate block">{owner.email}</span>}
                    </div>
                  </div>
                  <button type="button" onClick={() => handleRemoveOwner(owner.id)} className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all shrink-0">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              {assignedEigentuemer.length === 0 && (
                <div className="text-center py-3">
                  <Shield size={16} className="mx-auto text-gray-200 mb-1" />
                  <p className="text-[8px] text-gray-300 font-bold uppercase tracking-widest">Kein Eigentümer</p>
                </div>
              )}
            </div>
            <div className="relative pt-2" ref={ownerSearchRef}>
              <label className="text-[8px] font-black text-[#c4a77d] uppercase tracking-widest mb-1 block">Eigentümer hinzufügen</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={13} />
                <input
                  type="text"
                  value={ownerSearchQuery}
                  onChange={(e) => { setOwnerSearchQuery(e.target.value); setIsOwnerSearchOpen(true); }}
                  onFocus={() => setIsOwnerSearchOpen(true)}
                  placeholder="Eigentümer suchen..."
                  className="w-full text-xs font-bold pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:border-[#c4a77d] transition-colors"
                />
              </div>
              {isOwnerSearchOpen && ownerSearchQuery.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-100 z-50 max-h-[140px] overflow-y-auto custom-scrollbar animate-in slide-in-from-top-1 duration-150">
                  {filteredSearch.length > 0 ? filteredSearch.map(e => {
                    const zusatz = ansprechpartnerZusatz(e);
                    return (
                    <button key={e.id} type="button" onClick={() => handleAddOwner(e)} className="w-full p-2.5 flex items-center gap-2 hover:bg-[#fdfbf8] text-left border-b border-gray-50 last:border-0 transition-colors">
                      {istFirma(e) ? <Building2 size={12} className="text-gray-300 shrink-0" /> : <User size={12} className="text-gray-300 shrink-0" />}
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold text-gray-900 truncate">{eigentuemerAnzeigename(e)}</p>
                        {zusatz ? <p className="text-[9px] font-medium text-gray-400 truncate">{zusatz}</p> : <p className="text-[8px] text-gray-400 truncate">{e.email}</p>}
                      </div>
                    </button>
                    );
                  }) : <div className="p-3 text-center text-[9px] text-gray-300 font-bold">Nichts gefunden</div>}
                </div>
              )}
            </div>
          </Section>

          {/* Fläche & Einheiten */}
          <Section title="Fläche & Einheiten" icon={<Euro size={13} className="text-[#c4a77d]" />}>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <SidebarInput label="Einheiten" name="wohneinheiten" type="number" value={formData.wohneinheiten} onChange={handleChange} />
                <SidebarInput label="Wohnfläche (m²)" name="wohnflaeche" value={formData.wohnflaeche} onChange={handleChange} placeholder="z.B. 450" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <SidebarInput label="Baujahr" name="baujahr" type="number" value={formData.baujahr} onChange={handleChange} placeholder="z.B. 1990" />
                <SidebarInput label="Soll-Miete (€)" name="miete_soll" value={formData.miete_soll} onChange={handleChange} placeholder="z.B. 5.400" />
              </div>
            </div>
          </Section>

          {/* Technik & Energie */}
          <Section title="Technik & Energie" icon={<Activity size={13} className="text-[#c4a77d]" />}>
            <div className="space-y-3">
              <SidebarSelect label="Heizungsart" name="heizungsart" value={formData.heizungsart || ''} onChange={handleChange}
                options={['', 'Gas', 'Öl', 'Fernwärme', 'Wärmepumpe', 'Pellet', 'Elektro', 'Solar', 'Sonstige']} placeholder="Auswählen..." />
              <SidebarInput label="Energieausweis" name="energieausweis" value={formData.energieausweis} onChange={handleChange} placeholder="z.B. Verbrauchsausweis bis 12/2030" />
              <div className="grid grid-cols-2 gap-2">
                <SidebarSelect label="Energieklasse" name="energieklasse" value={formData.energieklasse || ''} onChange={handleChange}
                  options={['', 'A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']} placeholder="Auswählen..." />
                <SidebarInput label="Letzte Prüfung" name="letzte_pruefung" type="date" value={formData.letzte_pruefung} onChange={handleChange} />
              </div>
              <SidebarInput label="Ausstattung" name="ausstattung" value={formData.ausstattung} onChange={handleChange} placeholder="z.B. Aufzug, Balkon, Keller" />
              <div className="grid grid-cols-2 gap-2">
                <SidebarSelect label="Stellplatz" name="stellplatz_typ" value={formData.stellplatz_typ || 'Keine'} onChange={handleChange}
                  options={['Keine', 'Stellplatz', 'Garage', 'Tiefgarage', 'Carport']} />
                <SidebarInput label="Anzahl" name="stellplatz_anzahl" type="number" value={formData.stellplatz_anzahl} onChange={handleChange} />
              </div>
            </div>
          </Section>

          {/* Notizen */}
          <Section title="Notizen" icon={<Info size={13} className="text-[#c4a77d]" />}>
            <SidebarTextarea label="Interne Notizen" name="notizen" value={formData.notizen} onChange={handleChange} placeholder="Freitext für interne Vermerke..." />
          </Section>

          {/* Cloud & Medien */}
          <Section title="Cloud & Medien" icon={<Cloud size={13} className="text-[#c4a77d]" />}>
            <div className="space-y-3">
              <SidebarInput label="OneDrive-Link" name="onedrive_link" value={formData.onedrive_link} onChange={handleChange} placeholder="https://..." />
              <SidebarInput label="Bild-URL" name="bild_url" value={formData.bild_url} onChange={handleChange} placeholder="https://..." />
              {formData.bild_url && (
                <div className="rounded-xl overflow-hidden border border-gray-100 h-20">
                  <img src={formData.bild_url} alt="Vorschau" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                </div>
              )}
            </div>
          </Section>

          <div className="h-2" />
        </div>

        {/* ── Footer ── */}
        <div className="px-4 py-3 bg-white border-t border-gray-100 flex gap-2.5 shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 px-3 py-3 bg-gray-50 rounded-xl text-[9px] font-black uppercase tracking-widest text-gray-400 hover:bg-gray-100 active:scale-[0.97] transition-all">
            Abbrechen
          </button>
          <button onClick={handleSubmit} disabled={loading}
            className="flex-[2] px-3 py-3 bg-gradient-to-r from-[#c4a77d] to-[#b8956a] text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-[#c4a77d]/20 flex items-center justify-center gap-2 hover:shadow-[#c4a77d]/40 active:scale-[0.97] transition-all disabled:opacity-50">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            <span>Speichern</span>
          </button>
        </div>
      </div>
    </>
  );
};

// ── Section ──
const Section = ({ title, icon, badge, children }: { title: string; icon: React.ReactNode; badge?: string; children: React.ReactNode }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-2">
      {icon}
      <span className="text-[10px] font-black text-gray-600 uppercase tracking-[0.12em] flex-1">{title}</span>
      {badge && <span className="px-1.5 py-0.5 rounded-md bg-[#c4a77d]/10 text-[#c4a77d] text-[8px] font-black min-w-[18px] text-center">{badge}</span>}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

// ── Input ──
const SidebarInput = ({ label, name, value, onChange, type = 'text', required = false, placeholder = '' }: any) => (
  <div className="space-y-1">
    <label className="text-[8px] font-black text-[#c4a77d] uppercase tracking-widest block">{label}{required && ' *'}</label>
    <input
      type={type}
      name={name}
      value={value ?? ''}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-900 outline-none focus:border-[#c4a77d] focus:ring-2 focus:ring-[#c4a77d]/10 transition-all placeholder:text-gray-300 placeholder:font-medium"
    />
  </div>
);

// ── Select ──
const SidebarSelect = ({ label, name, value, onChange, options, placeholder }: any) => (
  <div className="space-y-1">
    <label className="text-[8px] font-black text-[#c4a77d] uppercase tracking-widest block">{label}</label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-900 outline-none appearance-none focus:border-[#c4a77d] focus:ring-2 focus:ring-[#c4a77d]/10 transition-all"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.filter((o: string) => o !== '').map((o: string) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

// ── Textarea ──
const SidebarTextarea = ({ label, name, value, onChange, placeholder = '' }: any) => (
  <div className="space-y-1">
    <label className="text-[8px] font-black text-[#c4a77d] uppercase tracking-widest block">{label}</label>
    <textarea
      name={name}
      value={value ?? ''}
      onChange={onChange}
      placeholder={placeholder}
      rows={3}
      className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-900 outline-none focus:border-[#c4a77d] focus:ring-2 focus:ring-[#c4a77d]/10 transition-all placeholder:text-gray-300 placeholder:font-medium resize-none"
    />
  </div>
);

export default ObjektEditSidebar;
